// Bilingual (en/he) string lookup for the Hub.
//
// KEYS ARE THE ENGLISH SOURCE STRINGS. t("Deep Scan"), not t("scan.deep").
// That means: no invented key names to keep in sync, and a missing Hebrew
// entry degrades to readable English instead of a raw key leaking into the UI.
//
// This module is imported by src/app.jsx (bundled by esbuild) and its catalog
// is also require()d by i18n-server.js for server-generated text.
//
// The UI is permanently English/LTR: there is no Hebrew catalog and no language
// switcher. useTranslated() still translates Hebrew user DATA to English.
export const HE = {};

const CATALOGS = { en: null }; // en is identity — no table needed
let _lang = "en";

/** The UI is English-only: always "en". */
export function detectLang(v) {
  return "en";
}

export function setCatalog(lang) { _lang = detectLang(lang); }
export function getLang() { return _lang; }

// {n} → vars.n. A placeholder with no matching var is left literal (better a
// visible {n} in one string than a crash or a silent "undefined").
function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m));
}

/**
 * t("Save")                       → "Save"
 * t("Ran {n} tasks", {n: 3})      → "Ran 3 tasks"
 * t({one:"1 task", other:"{n} tasks"}, {n}) → plural-aware
 */
export function t(key, vars) {
  if (key == null) return "";
  // Plural form: pick the English variant first, then translate THAT string,
  // so the catalog stores flat "1 task"/"{n} tasks" rows like everything else.
  if (typeof key === "object") {
    const n = vars && vars.n;
    key = Number(n) === 1 ? key.one : key.other;
    if (key == null) return "";
  }
  const src = String(key);
  if (!src) return "";
  const cat = CATALOGS[_lang];
  const hit = cat && Object.prototype.hasOwnProperty.call(cat, src) ? cat[src] : src;
  return interpolate(hit, vars);
}

// ── React binding ───────────────────────────────────────────────────────────
// React is a CDN global (see public/index.html), not an import — this module is
// bundled by esbuild but React is not part of that bundle.
const R = typeof window !== "undefined" ? window.React : null;

export const LangContext = R ? R.createContext({ lang: "en", setLang: () => {} }) : null;

export function useLang() { return R.useContext(LangContext); }

/** The UI is permanently English/LTR. */
function applyDocumentLang() {
  if (typeof document === "undefined") return;
  document.documentElement.dir = "ltr";
  document.documentElement.lang = "en";
}

export function LangProvider({ children }) {
  // The UI is English-only: no state, no server fetch, no language switcher.
  R.useEffect(() => {
    setCatalog("en");
    applyDocumentLang();
  }, []);

  const setLang = R.useCallback(() => {}, []);

  return R.createElement(LangContext.Provider, { value: { lang: "en", setLang } }, children);
}

// Same detector the chat direction logic uses — any Hebrew character means the
// text is Hebrew. Duplicated here (rather than imported from app.jsx) so this
// module has no dependency on the app root.
const HEB_RE = /[֐-׿]/;

/**
 * Lazily translate STABLE user content into the current UI language.
 *
 * Returns `text` immediately and swaps in the translation when it lands, so a
 * slow model call never blocks a paint. Text already in the UI language is
 * never sent anywhere. NOT for chat messages — see spec §5.
 */
export function useTranslated(text) {
  const { lang } = useLang();
  const [out, setOut] = R.useState(text);

  R.useEffect(() => {
    setOut(text);
    const src = String(text == null ? "" : text);
    if (!src.trim()) return;

    // Already in the UI language → nothing to do, no request.
    const isHebrew = HEB_RE.test(src);
    if ((lang === "he") === isHebrew) return;

    let live = true;
    fetch("/api/translate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: "x", text: src }], to: lang }),
    })
      .then(r => r.json())
      .then(d => {
        const hit = d && d.ok && d.results && d.results[0];
        if (live && hit && hit.text) setOut(hit.text);
      })
      .catch(() => {}); // a failed translation leaves the original standing

    return () => { live = false; };
  }, [text, lang]);

  return out;
}
